import React, {
  useCallback,
  useContext,
  useState,
  Fragment,
  useEffect,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { set, lensProp } from "ramda";
import { allTrue, notBlank } from "../../util/ext";
import { useIsAdmin } from "../../auth/authUtils";
import { useCreatorDispatch } from "../CreatorContext";
import { MediaContext } from "./media-context";
import { setAspectRatio } from "../../util/mediaUtil";

import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import EditIcon from "@material-ui/icons/Edit";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";

import EditColumn from "./EditColumn";
import Image from "../../components/Image";
import ThumbnailDialog from "./ThumbnailDialog";

const useStyles = makeStyles(theme => ({
  root: {
    paddingLeft: "0",
    width: "165px",
  },

  thumbnailOverlay: {
    height: "150px",
    width: "150px",
    position: "relative",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
  },

  thumbnail: {
    height: "150px",
    width: "150px",
    borderRadius: "20px",
    objectFit: "contain",
  },

  thumbnailEdit: {
    position: "absolute",
    bottom: "10px",
    right: "10px",
  },

  emptyThumbnail: {
    fontSize: "120px",
    color: theme.palette.primary.disabled,
    position: "absolute",
    right: "15px",
    top: "15px",
  },

  editIcon: {
    color: "white",
  },

  locked: {
    marginLeft: theme.spacing(2),
  },
}));

const bustCache = url => `${url}?tc=${Date.now()}`;

const EditThumbnailOverlay = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();
  const {
    accept,
    showAlert,
    uploader,
    hasThumbnailLock,
    editable = true,
  } = props;
  const { addUpdatedThumbnail } = useCreatorDispatch();

  const [thumbnailSrc, setThumbnailSrc] = useState(null);
  useEffect(() => {
    if (notBlank(context.thumbnailUrl)) {
      setThumbnailSrc(context.thumbnailUrl);
    } else if (notBlank(context.media.thumbnailUrl)) {
      setThumbnailSrc(bustCache(context.media.thumbnailUrl));
    } else {
      setThumbnailSrc(null);
    }
  }, [context.media.thumbnailUrl, context.thumbnailUrl]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onEditClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleSave = useCallback(
    (url, file) => {
      setUploading(true);
      console.log("Uploading...");
      uploader.mutate({
        resourceIds: { id: context.media.id },
        data: file,
      });

      if (!uploader.isLoading) {
        console.log("Upload Complete...");
        setUploading(false);
        if (uploader.isError) {
          showAlert("error", [uploader.error.message]);
        } else {
          setDialogOpen(false);
          context.setThumbnailUrl(url);
          addUpdatedThumbnail(context.media.id);
          showAlert("success", ["Content Successfully Saved"]);
        }
      }
    },
    [addUpdatedThumbnail, context, showAlert, uploader],
  );

  const formBuilder = useCallback(
    handlers => {
      return (
        <Fragment>
          <Box classes={{ root: classes.thumbnailOverlay }}>
            <Image alt={context.media.name} src={thumbnailSrc} />

            {editable && (
              <IconButton
                color="primary"
                classes={{ root: classes.thumbnailEdit }}
                onClick={onEditClick}
              >
                <EditIcon
                  fontSize="large"
                  classes={{ root: classes.editIcon }}
                />
              </IconButton>
            )}
            <ThumbnailDialog
              aspectRatio={setAspectRatio(context.media)}
              media={context.media}
              visible={dialogOpen}
              uploading={uploading}
              currentUrl={context.media.thumbnailUrl || context.thumbnailUrl}
              accept={accept}
              hasThumbnailLock={hasThumbnailLock}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          </Box>
          {allTrue([hasThumbnailLock, isAdmin]) && (
            <div className={classes.locked}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={context.media.thumbnailLocked}
                    onChange={event => {
                      const value = event.target.checked;
                      context.setMedia(prev =>
                        set(lensProp("thumbnailLocked"), value, prev),
                      );
                    }}
                    name="content-thumbnailLocked"
                    inputProps={{ "aria-label": "Select" }}
                  />
                }
                label="Lock thumbnail"
              />
            </div>
          )}
        </Fragment>
      );
    },
    [
      isAdmin,
      accept,
      context,
      classes,
      dialogOpen,
      editable,
      uploading,
      handleSave,
      handleCancel,
      onEditClick,
      hasThumbnailLock,
      thumbnailSrc,
    ],
  );

  return <EditColumn className={classes.root} formBuilder={formBuilder} />;
};

export default EditThumbnailOverlay;
